/**
 * Admin — KCIS Adaptive Sessions list (Sprint C)
 *
 * Mounts at /admin/kcis-adaptive-sessions. Super-admin only — uses the same
 * `adminAuth` guard the existing wellness/funnel admin pages use.
 *
 * Surfaces:
 *   - `GET  /api/admin/kcis/assessment-sessions`           (paginated list)
 *   - `GET  /api/admin/kcis/assessment-sessions/{id}`      (single)
 *   - `GET  /api/admin/kcis/export/sessions.csv`           (CSV stream)
 *
 * Filters: source domain, assigned door. Includes engine version chip to
 * distinguish adaptive (1.0.0) from `legacy_shim_1.0` shadow records.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, adminAuth, API } from "@/lib/api";
import { formatApiError } from "@/lib/formatError";
import { TopNav, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";

const PAGE_SIZE = 25;
const SOURCE_OPTIONS = ["", "christyvonta.com", "kissedbycvon.com", "kisseduniversity.com", "direct"];
const DOOR_OPTIONS = ["", "clinic", "coaching", "university", "comfort_care", "integrated"];

export default function AdminKcisAdaptiveSessions() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [source, setSource] = useState("");
  const [door, setDoor] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    if (!adminAuth.getToken()) { navigate("/admin/login"); return; }
    if (!adminAuth.isSuperAdmin?.()) { navigate("/admin"); return; }
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) });
      if (source) params.set("source", source);
      if (door) params.set("door", door);
      const r = await api.get(`/admin/kcis/assessment-sessions?${params.toString()}`);
      setItems(r.data.items || []); setTotal(r.data.total || 0);
    } catch (e) {
      setError(formatApiError(e, "Failed to load sessions"));
    } finally {
      setLoading(false);
    }
  }, [page, source, door]);

  useEffect(() => { load(); }, [load]);

  async function openSession(id) {
    try {
      const r = await api.get(`/admin/kcis/assessment-sessions/${id}`);
      setSelected(r.data);
      setSheetOpen(true);
    } catch (e) {
      setError(formatApiError(e, "Failed to load session"));
    }
  }

  async function exportCsv() {
    try {
      const token = adminAuth.getToken?.();
      const url = `${API}/admin/kcis/export/sessions.csv`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `kcis-adaptive-sessions-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) {
      setError(formatApiError(e, "Export failed"));
    }
  }

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <TopNav />
      <main className="flex-1 max-w-7xl mx-auto px-6 py-10 w-full" data-testid="admin-kcis-sessions-page">
        <header className="mb-6 flex items-end justify-between gap-3 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-widest text-purple-900 font-semibold">KCIS Adaptive — Sprint C</p>
            <h1 className="text-2xl sm:text-3xl font-semibold mt-1">Adaptive Sessions</h1>
            <p className="text-sm text-slate-600 mt-1">All sessions captured by the adaptive engine + legacy shim shadows.</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={exportCsv} data-testid="admin-kcis-sessions-export" variant="outline">Export CSV</Button>
            <Button onClick={() => navigate("/admin/kcis-adaptive-reporting")} data-testid="admin-kcis-sessions-reporting-link">View reporting →</Button>
          </div>
        </header>

        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex flex-wrap gap-3 items-end" data-testid="admin-kcis-sessions-filters">
          <label className="text-sm flex flex-col gap-1">
            <span className="text-xs text-slate-500">Source domain</span>
            <select className="border rounded-md px-3 py-2 text-sm" value={source} onChange={(e) => { setPage(1); setSource(e.target.value); }} data-testid="admin-kcis-filter-source">
              {SOURCE_OPTIONS.map((s) => <option key={s} value={s}>{s || "All"}</option>)}
            </select>
          </label>
          <label className="text-sm flex flex-col gap-1">
            <span className="text-xs text-slate-500">Assigned door</span>
            <select className="border rounded-md px-3 py-2 text-sm" value={door} onChange={(e) => { setPage(1); setDoor(e.target.value); }} data-testid="admin-kcis-filter-door">
              {DOOR_OPTIONS.map((d) => <option key={d} value={d}>{d || "All"}</option>)}
            </select>
          </label>
          <span className="text-xs text-slate-500 ml-auto">{total} session{total === 1 ? "" : "s"}</span>
        </div>

        {error && <p className="text-rose-700 text-sm mb-3" data-testid="admin-kcis-sessions-error">{error}</p>}

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Started</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Door</TableHead>
                <TableHead>Readiness</TableHead>
                <TableHead>Archetype</TableHead>
                <TableHead>Engine</TableHead>
                <TableHead>UTM source</TableHead>
                <TableHead>Email</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-500">Loading…</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-500">No sessions match these filters.</TableCell></TableRow>
              ) : items.map((s) => (
                <TableRow key={s.id} data-testid="admin-kcis-sessions-row">
                  <TableCell className="text-xs whitespace-nowrap">{new Date(s.started_at).toLocaleString()}</TableCell>
                  <TableCell><Badge variant="secondary" className="text-xs">{s.source_domain}</Badge></TableCell>
                  <TableCell className="text-sm">{s.assigned_door || "—"}</TableCell>
                  <TableCell className="text-xs">{s.readiness?.band || "—"}</TableCell>
                  <TableCell className="text-xs">{s.emotional_identity_profile?.primary_archetype || "—"}</TableCell>
                  <TableCell className="text-xs">
                    <Badge variant={s.engine_version?.startsWith("legacy_shim") ? "outline" : "default"} className="text-[10px]">
                      {s.engine_version || "—"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">{s.marketing_attribution?.utm_source || "—"}</TableCell>
                  <TableCell className="text-xs">{s.email || "—"}</TableCell>
                  <TableCell><Button size="sm" variant="ghost" onClick={() => openSession(s.id)} data-testid="admin-kcis-sessions-view">View</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="flex justify-between items-center mt-4 text-sm">
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} data-testid="admin-kcis-page-prev">Prev</Button>
          <span>Page {page} / {pages}</span>
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages} data-testid="admin-kcis-page-next">Next</Button>
        </div>

        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="admin-kcis-sessions-sheet">
            <SheetHeader>
              <SheetTitle>Session detail</SheetTitle>
            </SheetHeader>
            {selected && (
              <pre className="text-xs bg-slate-50 p-3 rounded-md overflow-x-auto mt-4 whitespace-pre-wrap">
                {JSON.stringify(selected, null, 2)}
              </pre>
            )}
          </SheetContent>
        </Sheet>
      </main>
      <Footer />
    </div>
  );
}
